import React, { useState, useRef, useEffect } from 'react';
import { Modal, IconButton, Box, Typography, Divider, styled, Slider, CircularProgress } from '@mui/material';
import CloseIconCross from '../../../assets/img/close-icon-cross.svg';
import PlayIcon from '../../../assets/img/solar_play-bold.svg';
import PauseIcon from '../../../assets/img/solar_pause-bold.svg';

interface VideoPlayerProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
}

const StyledModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: '459px',
  maxHeight: '90vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.05)',
  outline: 'none',
  padding: '24px',
  boxSizing: 'border-box',
  border: '1px solid #E1E1E1',
  borderRadius: '5px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    gap: '12px',
  },
}));

const StyledCloseButton = styled(IconButton)({
  position: 'absolute',
  top: 16,
  right: 16,
  padding: '4px',
});

const CustomSlider = styled(Slider)({
  color: '#000000',
  height: 2,
  width: '100%',
  '& .MuiSlider-thumb': {
    width: 3,
    height: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 0,
    boxShadow: 'none',
    '&:focus, &:hover, &:active': {
      boxShadow: 'none',
    },
  },
  '& .MuiSlider-rail': {
    opacity: 1,
    backgroundColor: '#000000',
    height: 2,
  },
  '& .MuiSlider-track': {
    backgroundColor: '#000000',
    height: 2,
  },
});

const ControlsContainer = styled(Box)({
  position: 'absolute',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'calc(100% - 48px)',
  maxWidth: '387px',
  height: '40px',
  padding: '4px 16px',
  backgroundColor: '#6B6B6B',
  borderRadius: '100px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const TimeDisplay = styled(Typography)({
  color: '#FFFFFF',
  minWidth: '40px',
  fontSize: '14px',
  fontWeight: 500,
});

const ErrorContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center',
  color: '#FF0000',
  padding: '20px',
});

const LoadingContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
});

const VideoPlayer: React.FC<VideoPlayerProps> = ({ open, onClose, videoUrl }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset states when video URL changes
    setIsLoading(true);
    setError(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  }, [videoUrl]);

  const handlePlayPause = () => {
    if (!videoRef.current || error) return;

    try {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((e) => {
            console.error('Playback error:', e);
            setError('Failed to play video. Please try again.');
          });
        }
      }
      setIsPlaying(!isPlaying);
    } catch (e) {
      console.error('Play/Pause error:', e);
      setError('Video playback error occurred.');
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
    setIsLoading(false);
    setError(null);
  };

  const handleLoadedData = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error('Video error:', e);
    const video = e.currentTarget;
    let errorMessage = 'An error occurred while loading the video.';

    if (video.error) {
      switch (video.error.code) {
        case 1:
          errorMessage = 'Video loading aborted.';
          break;
        case 2:
          errorMessage = 'Network error occurred while loading video.';
          break;
        case 3:
          errorMessage = 'Video decoding failed.';
          break;
        case 4:
          errorMessage = 'Video not supported.';
          break;
        default:
          errorMessage = 'An unknown error occurred.';
          break;
      }
    }

    setError(errorMessage);
    setIsLoading(false);
    setIsPlaying(false);
  };

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    if (!videoRef.current || error) return;

    const newTime = Array.isArray(newValue) ? newValue[0] : newValue;
    try {
      const targetTime = (newTime / 100) * duration;
      videoRef.current.currentTime = targetTime;
      setCurrentTime(targetTime);
    } catch (e) {
      console.error('Seek error:', e);
      setError('Failed to seek video.');
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="video-player-modal"
      sx={{
        backdropFilter: 'blur(5px)',
        backgroundColor: '#0E183199',
      }}
    >
      <StyledModalBox>
        <StyledCloseButton onClick={onClose}>
          <Box component="img" src={CloseIconCross} alt="Close" sx={{ width: '28px', height: '28px' }} />
        </StyledCloseButton>

        <Typography variant="title2Bold" component="h2" id="video-player-modal">
          Preview
        </Typography>

        <Divider sx={{ width: '100%' }} />

        <Box sx={{ position: 'relative', width: '100%', paddingTop: '28px', paddingBottom: '28px' }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '400px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E1E1E1',
              borderRadius: '5px',
            }}
          >
            {isLoading && !error && (
              <LoadingContainer>
                <CircularProgress size={40} data-testid="progress-indicator" />
                <Typography>Loading video...</Typography>
              </LoadingContainer>
            )}

            {error && (
              <ErrorContainer>
                <Typography color="error" gutterBottom>
                  {error}
                </Typography>
                <Typography>Please try refreshing the page or contact support if the issue persists.</Typography>
              </ErrorContainer>
            )}

            <video
              data-testid="video-player"
              ref={videoRef}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: error ? 'none' : 'block',
              }}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onLoadedData={handleLoadedData}
              onError={handleError}
              onWaiting={() => setIsLoading(true)}
              onPlaying={() => setIsLoading(false)}
              playsInline
              aria-label="Gem analysis video"
              title="Gem analysis video"
            >
              <source src={videoUrl} type="video/mp4" />
              <source src={videoUrl.replace('.mp4', '.webm')} type="video/webm" />
              <track kind="captions" srcLang="en" label="English captions" default />
              Your browser does not support the video tag.
            </video>

            {!error && (
              <ControlsContainer>
                <IconButton
                  onClick={handlePlayPause}
                  disabled={isLoading || !!error}
                  sx={{ padding: 0, width: '24px', height: '24px' }}
                  data-testid="play-pause-button"
                  aria-label={isPlaying ? 'Pause video' : 'Play video'}
                >
                  <Box
                    component="img"
                    src={isPlaying ? PauseIcon : PlayIcon}
                    sx={{ width: '24px', height: '24px' }}
                    alt={isPlaying ? 'Pause' : 'Play'}
                  />
                </IconButton>

                <CustomSlider
                  value={(currentTime / duration) * 100 || 0}
                  onChange={handleSliderChange}
                  disabled={isLoading || !!error}
                  aria-label="Video progress"
                  aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
                />

                <TimeDisplay>{duration ? formatTime(currentTime) : '0:00'}</TimeDisplay>
              </ControlsContainer>
            )}
          </Box>
        </Box>
      </StyledModalBox>
    </Modal>
  );
};

export default VideoPlayer;
